import { useState, useEffect } from "react";
import trans from "../utils";

/**
 * Reusable hook to handle data fetching for alert popups.
 * @param {Function} hook - The RTK Query hook (e.g. useGetAllTasksQuery).
 * @param {Object} initialParams - Default query parameters (e.g. { page: 1, limit: 10 }).
 */
export default function useAlertData(
  hook,
  initialParams = { page: 1, limit: 10 }
) {
  const [params, setParams] = useState(initialParams);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  // Call the provided API hook dynamically
  const { data, isLoading, isError, refetch } = hook(params);

  // Update when params change
  useEffect(() => {
    refetch();
  }, [params, refetch]);

  useEffect(() => {
    if (isError && retryCount < 3) {
      const retryDelay = 1000 * (retryCount + 1); // wait longer each retry
      const timer = setTimeout(() => {
        setRetryCount((prev) => prev + 1);
        refetch();
      }, retryDelay);
      return () => clearTimeout(timer);
    }

    if (isError && retryCount >= 3) {
      setError(trans("Error happened while fetching data, please try again later."));
    }

    if (!isError && data) {
      setError(null);
      setRetryCount(0); // reset on success
    }
  }, [isError, retryCount, refetch, data]);

  // Helper functions to change pagination or filters
  const onPageChange = (page) => setParams((prev) => ({ ...prev, page }));
  const onLimitChange = (limit) =>
    setParams((prev) => ({ ...prev, limit, page: 1 }));
  const onFilterChange = (filters) =>
    setParams((prev) => ({ ...prev, ...filters }));

  return {
    data: data || [],
    total: data?.total || 0,
    isLoading,
    params,
    onPageChange,
    onLimitChange,
    onFilterChange,
    error,
  };
}
